/**
 * A QR code, from nothing.
 *
 * ── Why this is written out rather than installed ────────────────────────
 *
 * Drawing one QR code is not worth a dependency in a till's bundle: a package
 * means a `yarn add` somebody has to remember on every machine that builds
 * this, a licence to carry, and one more thing that can go stale. Everything
 * below is the ISO/IEC 18004 spec for a plain QR code and it does not change,
 * so writing it once costs less than depending on it forever.
 *
 * ── What it makes ────────────────────────────────────────────────────────
 *
 * Model 2, byte mode, error correction level M — the middle level, which can
 * lose about 15% of the code and still read. That matters here: these get
 * printed, taped to a table, and read across a year of spilled tea.
 *
 * Versions 1 to 6 only, which hold up to 106 bytes. That is far more than any
 * menu link needs (`https://menu.example.com/Vb3Wu34JaM` is 37), and stopping
 * at 6 means no version-information block — the 18-bit field only versions 7
 * and up carry. One fewer table to get wrong.
 *
 * ── How it was checked ───────────────────────────────────────────────────
 *
 * Not by eye. Every matrix this file produces was rendered to a PNG and read
 * back with OpenCV's QR decoder, at several module sizes, for every version
 * from 1 to 6 and every payload length up to the limit. A phone camera and
 * OpenCV are looking for the same thing, so a code OpenCV reads is a code a
 * phone reads.
 */

/* ── the tables ─────────────────────────────────────────────────────────── */

/**
 * Per version, at error-correction level M: how many blocks the data is split
 * into, how many data codewords are in each, and how many error-correction
 * codewords are generated per block.
 *
 * For versions 1 to 6 at level M every block is the same size, which is why
 * there is one entry rather than the two groups larger codes need.
 */
const LEVEL_M = {
  1: { blocks: 1, dataPerBlock: 16, ecPerBlock: 10 },
  2: { blocks: 1, dataPerBlock: 28, ecPerBlock: 16 },
  3: { blocks: 1, dataPerBlock: 44, ecPerBlock: 26 },
  4: { blocks: 2, dataPerBlock: 32, ecPerBlock: 18 },
  5: { blocks: 2, dataPerBlock: 43, ecPerBlock: 24 },
  6: { blocks: 4, dataPerBlock: 27, ecPerBlock: 16 },
};

/** Centres of the alignment patterns. Version 1 has none. */
const ALIGNMENT = {
  1: [],
  2: [6, 18],
  3: [6, 22],
  4: [6, 26],
  5: [6, 30],
  6: [6, 34],
};

/** Bits left over after the codewords, which are written as zeroes. */
const REMAINDER_BITS = { 1: 0, 2: 7, 3: 7, 4: 7, 5: 7, 6: 7 };

const MAX_VERSION = 6;

/* ── GF(256) ────────────────────────────────────────────────────────────── */

/**
 * The field the error-correction maths happens in: 256 values, with the
 * primitive polynomial x⁸+x⁴+x³+x²+1 (0x11D) that the spec names.
 *
 * Multiplication becomes addition of logarithms, so both tables are built
 * once here and the rest of the file just looks values up.
 */
const EXP = new Uint8Array(512);
const LOG = new Uint8Array(256);

(function buildTables() {
  let x = 1;
  for (let i = 0; i < 255; i += 1) {
    EXP[i] = x;
    LOG[x] = i;
    x <<= 1;
    if (x & 0x100) x ^= 0x11d;
  }
  for (let i = 255; i < 512; i += 1) EXP[i] = EXP[i - 255];
})();

function gfMultiply(a, b) {
  if (a === 0 || b === 0) return 0;
  return EXP[LOG[a] + LOG[b]];
}

/**
 * The generator polynomial for `degree` error-correction codewords:
 * (x − α⁰)(x − α¹)…(x − α^(degree−1)), multiplied out.
 */
function generatorPolynomial(degree) {
  let poly = [1];
  for (let i = 0; i < degree; i += 1) {
    const next = new Array(poly.length + 1).fill(0);
    for (let j = 0; j < poly.length; j += 1) {
      next[j] ^= poly[j];
      next[j + 1] ^= gfMultiply(poly[j], EXP[i]);
    }
    poly = next;
  }
  return poly;
}

/** The error-correction codewords for one block: polynomial long division. */
function errorCorrectionFor(block, ecCount) {
  const generator = generatorPolynomial(ecCount);
  const remainder = new Array(block.length + ecCount).fill(0);
  for (let i = 0; i < block.length; i += 1) remainder[i] = block[i];

  for (let i = 0; i < block.length; i += 1) {
    const lead = remainder[i];
    if (lead === 0) continue;
    for (let j = 0; j < generator.length; j += 1) {
      remainder[i + j] ^= gfMultiply(generator[j], lead);
    }
  }

  return remainder.slice(block.length);
}

/* ── the payload ────────────────────────────────────────────────────────── */

/** UTF-8 bytes. QR byte mode is bytes, not characters. */
function toBytes(text) {
  const out = [];
  for (let i = 0; i < text.length; i += 1) {
    let code = text.charCodeAt(i);

    // Surrogate pair -> one code point.
    if (code >= 0xd800 && code <= 0xdbff && i + 1 < text.length) {
      const low = text.charCodeAt(i + 1);
      if (low >= 0xdc00 && low <= 0xdfff) {
        code = 0x10000 + ((code - 0xd800) << 10) + (low - 0xdc00);
        i += 1;
      }
    }

    if (code < 0x80) {
      out.push(code);
    } else if (code < 0x800) {
      out.push(0xc0 | (code >> 6), 0x80 | (code & 0x3f));
    } else if (code < 0x10000) {
      out.push(0xe0 | (code >> 12), 0x80 | ((code >> 6) & 0x3f), 0x80 | (code & 0x3f));
    } else {
      out.push(
        0xf0 | (code >> 18),
        0x80 | ((code >> 12) & 0x3f),
        0x80 | ((code >> 6) & 0x3f),
        0x80 | (code & 0x3f)
      );
    }
  }
  return out;
}

/** The smallest version that fits. Byte mode costs 12 bits of header here. */
function versionFor(byteLength) {
  for (let version = 1; version <= MAX_VERSION; version += 1) {
    const spec = LEVEL_M[version];
    const capacity = spec.blocks * spec.dataPerBlock;
    // 4 bits of mode + 8 bits of length; versions 1-9 use an 8-bit length.
    if (byteLength + 2 <= capacity) return version;
  }
  return 0;
}

/** Mode indicator, length, the bytes, a terminator, then the pad pattern. */
function buildDataCodewords(bytes, version) {
  const spec = LEVEL_M[version];
  const capacity = spec.blocks * spec.dataPerBlock;

  const bits = [];
  const push = (value, width) => {
    for (let i = width - 1; i >= 0; i -= 1) bits.push((value >> i) & 1);
  };

  push(0b0100, 4); // byte mode
  push(bytes.length, 8); // length, 8 bits for versions 1-9
  bytes.forEach((byte) => push(byte, 8));

  // Terminator: up to four zero bits, fewer if the code is nearly full.
  const room = capacity * 8 - bits.length;
  push(0, Math.min(4, room));

  // Pad to a whole byte.
  while (bits.length % 8 !== 0) bits.push(0);

  const codewords = [];
  for (let i = 0; i < bits.length; i += 8) {
    let byte = 0;
    for (let j = 0; j < 8; j += 1) byte = (byte << 1) | bits[i + j];
    codewords.push(byte);
  }

  // The two pad codewords the spec names, alternating, until it is full.
  const PAD = [0xec, 0x11];
  let padIndex = 0;
  while (codewords.length < capacity) {
    codewords.push(PAD[padIndex % 2]);
    padIndex += 1;
  }

  return codewords;
}

/**
 * Split into blocks, generate error correction for each, then interleave:
 * the first codeword of every block, then the second of every block, and so
 * on — data first, then error correction. Interleaving is what lets a code
 * survive a scratch across it rather than losing one whole block.
 */
function interleave(dataCodewords, version) {
  const spec = LEVEL_M[version];

  const dataBlocks = [];
  const ecBlocks = [];
  for (let i = 0; i < spec.blocks; i += 1) {
    const block = dataCodewords.slice(i * spec.dataPerBlock, (i + 1) * spec.dataPerBlock);
    dataBlocks.push(block);
    ecBlocks.push(errorCorrectionFor(block, spec.ecPerBlock));
  }

  const out = [];
  for (let i = 0; i < spec.dataPerBlock; i += 1) {
    for (let b = 0; b < spec.blocks; b += 1) out.push(dataBlocks[b][i]);
  }
  for (let i = 0; i < spec.ecPerBlock; i += 1) {
    for (let b = 0; b < spec.blocks; b += 1) out.push(ecBlocks[b][i]);
  }
  return out;
}

/* ── the grid ───────────────────────────────────────────────────────────── */

const MASKS = [
  (r, c) => (r + c) % 2 === 0,
  (r) => r % 2 === 0,
  (r, c) => c % 3 === 0,
  (r, c) => (r + c) % 3 === 0,
  (r, c) => (Math.floor(r / 2) + Math.floor(c / 3)) % 2 === 0,
  (r, c) => ((r * c) % 2) + ((r * c) % 3) === 0,
  (r, c) => (((r * c) % 2) + ((r * c) % 3)) % 2 === 0,
  (r, c) => (((r * c) % 3) + ((r + c) % 2)) % 2 === 0,
];

function blankGrid(size) {
  const grid = [];
  for (let r = 0; r < size; r += 1) grid.push(new Array(size).fill(null));
  return grid;
}

/** The three big squares, and the blank ring that separates each from the data. */
function placeFinder(grid, row, col) {
  const size = grid.length;
  for (let r = -1; r <= 7; r += 1) {
    for (let c = -1; c <= 7; c += 1) {
      const gr = row + r;
      const gc = col + c;
      if (gr < 0 || gr >= size || gc < 0 || gc >= size) continue;
      const onEdge = (r >= 0 && r <= 6 && (c === 0 || c === 6)) ||
        (c >= 0 && c <= 6 && (r === 0 || r === 6));
      const inCentre = r >= 2 && r <= 4 && c >= 2 && c <= 4;
      grid[gr][gc] = onEdge || inCentre;
    }
  }
}

function placeAlignment(grid, version) {
  const centres = ALIGNMENT[version];
  for (let i = 0; i < centres.length; i += 1) {
    for (let j = 0; j < centres.length; j += 1) {
      const row = centres[i];
      const col = centres[j];
      // Not over a finder pattern.
      if (grid[row][col] !== null) continue;
      for (let r = -2; r <= 2; r += 1) {
        for (let c = -2; c <= 2; c += 1) {
          grid[row + r][col + c] =
            Math.abs(r) === 2 || Math.abs(c) === 2 || (r === 0 && c === 0);
        }
      }
    }
  }
}

function placeTiming(grid) {
  const size = grid.length;
  for (let i = 8; i < size - 8; i += 1) {
    const on = i % 2 === 0;
    if (grid[6][i] === null) grid[6][i] = on;
    if (grid[i][6] === null) grid[i][6] = on;
  }
}

/** Keep the format-information cells out of the data placement's way. */
function reserveFormatAreas(grid) {
  const size = grid.length;
  for (let i = 0; i < 9; i += 1) {
    if (grid[8][i] === null) grid[8][i] = false;
    if (grid[i][8] === null) grid[i][8] = false;
  }
  for (let i = 0; i < 8; i += 1) {
    if (grid[8][size - 1 - i] === null) grid[8][size - 1 - i] = false;
    if (grid[size - 1 - i][8] === null) grid[size - 1 - i][8] = false;
  }
  grid[size - 8][8] = true; // the one module that is always dark
}

/**
 * The format information: which error-correction level and which mask, twice
 * over so a damaged corner does not make the code unreadable.
 *
 * Computed rather than looked up. The BCH(15,5) remainder and the fixed mask
 * the spec applies afterwards are three lines of arithmetic; a table of eight
 * hand-copied bit strings is three lines of arithmetic plus a typo waiting to
 * happen.
 */
function formatBits(maskIndex) {
  const LEVEL_BITS = 0b00; // level M
  const data = (LEVEL_BITS << 3) | maskIndex;

  let remainder = data << 10;
  for (let i = 4; i >= 0; i -= 1) {
    if (remainder & (1 << (i + 10))) remainder ^= 0b10100110111 << i;
  }

  return ((data << 10) | remainder) ^ 0b101010000010010;
}

function placeFormat(grid, maskIndex) {
  const size = grid.length;
  const bits = formatBits(maskIndex);

  for (let i = 0; i < 15; i += 1) {
    const on = ((bits >> i) & 1) === 1;

    // Down the left-hand side of the top-left finder.
    if (i < 6) grid[i][8] = on;
    else if (i < 8) grid[i + 1][8] = on;
    else grid[size - 15 + i][8] = on;

    // Along the top, and along the top-right.
    if (i < 8) grid[8][size - i - 1] = on;
    else if (i < 9) grid[8][15 - i - 1 + 1] = on;
    else grid[8][15 - i - 1] = on;
  }

  grid[size - 8][8] = true;
}

/**
 * The data, written into whatever cells are left.
 *
 * Two columns at a time, from the bottom-right corner, snaking up and then
 * down, skipping the vertical timing column. The mask is applied as each
 * module is written rather than afterwards, because only data modules are
 * masked — the patterns must not be.
 */
function placeData(grid, codewords, maskIndex) {
  const size = grid.length;
  const mask = MASKS[maskIndex];

  let bitIndex = 7;
  let byteIndex = 0;
  let row = size - 1;
  let direction = -1;

  for (let col = size - 1; col > 0; col -= 2) {
    if (col === 6) col -= 1; // the timing column is not data

    for (;;) {
      for (let c = 0; c < 2; c += 1) {
        const target = col - c;
        if (grid[row][target] !== null) continue;

        let dark = false;
        if (byteIndex < codewords.length) {
          dark = ((codewords[byteIndex] >>> bitIndex) & 1) === 1;
        }
        if (mask(row, target)) dark = !dark;

        grid[row][target] = dark;

        bitIndex -= 1;
        if (bitIndex === -1) {
          byteIndex += 1;
          bitIndex = 7;
        }
      }

      row += direction;
      if (row < 0 || row >= size) {
        row -= direction;
        direction = -direction;
        break;
      }
    }
  }
}

/* ── choosing a mask ────────────────────────────────────────────────────── */

/**
 * The four penalties from the spec, added up. The mask with the lowest total
 * wins — it is the one that leaves the fewest large blocks of one colour and
 * the fewest patterns a scanner might mistake for a finder.
 */
function penalty(grid) {
  const size = grid.length;
  let score = 0;

  // 1 — runs of five or more of the same colour, in both directions.
  for (let r = 0; r < size; r += 1) {
    let run = 1;
    for (let c = 1; c < size; c += 1) {
      if (grid[r][c] === grid[r][c - 1]) {
        run += 1;
        if (run === 5) score += 3;
        else if (run > 5) score += 1;
      } else run = 1;
    }
  }
  for (let c = 0; c < size; c += 1) {
    let run = 1;
    for (let r = 1; r < size; r += 1) {
      if (grid[r][c] === grid[r - 1][c]) {
        run += 1;
        if (run === 5) score += 3;
        else if (run > 5) score += 1;
      } else run = 1;
    }
  }

  // 2 — every 2x2 block of one colour.
  for (let r = 0; r < size - 1; r += 1) {
    for (let c = 0; c < size - 1; c += 1) {
      const v = grid[r][c];
      if (v === grid[r][c + 1] && v === grid[r + 1][c] && v === grid[r + 1][c + 1]) {
        score += 3;
      }
    }
  }

  // 3 — the finder-like sequence, with four light modules on either side.
  const A = [true, false, true, true, true, false, true, false, false, false, false];
  const B = [false, false, false, false, true, false, true, true, true, false, true];
  const matches = (get, start) => {
    let a = true;
    let b = true;
    for (let i = 0; i < 11; i += 1) {
      const v = get(start + i);
      if (v !== A[i]) a = false;
      if (v !== B[i]) b = false;
    }
    return a || b;
  };
  for (let r = 0; r < size; r += 1) {
    for (let c = 0; c + 11 <= size; c += 1) {
      if (matches((i) => grid[r][i], c)) score += 40;
    }
  }
  for (let c = 0; c < size; c += 1) {
    for (let r = 0; r + 11 <= size; r += 1) {
      if (matches((i) => grid[i][c], r)) score += 40;
    }
  }

  // 4 — how far the proportion of dark modules strays from half.
  let dark = 0;
  for (let r = 0; r < size; r += 1) {
    for (let c = 0; c < size; c += 1) if (grid[r][c]) dark += 1;
  }
  const percent = (dark * 100) / (size * size);
  score += Math.floor(Math.abs(percent - 50) / 5) * 10;

  return score;
}

/* ── the one thing this file exports ────────────────────────────────────── */

/**
 * Encode a string as a QR code.
 *
 * @param {string} text  what the code should say — here, a URL
 * @returns {{ size: number, modules: boolean[][] }}
 *          `size` modules square; `modules[row][col]` is true where the code
 *          is dark. The quiet zone is NOT included — drawing code adds it.
 * @throws  if the text is longer than a version 6 code can hold
 */
export default function encodeQr(text) {
  const bytes = toBytes(String(text == null ? '' : text));
  const version = versionFor(bytes.length);

  if (!version) {
    throw new Error(
      `Too long for a QR code of this size: ${bytes.length} bytes, limit ${
        LEVEL_M[MAX_VERSION].blocks * LEVEL_M[MAX_VERSION].dataPerBlock - 2
      }.`
    );
  }

  const codewords = interleave(buildDataCodewords(bytes, version), version);
  const size = version * 4 + 17;

  let best = null;
  let bestScore = Infinity;

  for (let maskIndex = 0; maskIndex < 8; maskIndex += 1) {
    const grid = blankGrid(size);

    placeFinder(grid, 0, 0);
    placeFinder(grid, 0, size - 7);
    placeFinder(grid, size - 7, 0);
    placeAlignment(grid, version);
    placeTiming(grid);
    reserveFormatAreas(grid);
    placeData(grid, codewords, maskIndex);
    placeFormat(grid, maskIndex);

    // The remainder bits are already null-free after placeData; anything
    // still unset would be a bug, so it is pinned light rather than left to
    // render as a hole.
    for (let r = 0; r < size; r += 1) {
      for (let c = 0; c < size; c += 1) if (grid[r][c] === null) grid[r][c] = false;
    }

    const score = penalty(grid);
    if (score < bestScore) {
      bestScore = score;
      best = grid;
    }
  }

  return { size, modules: best, version };
}

/** How many bytes a code can carry, for callers that want to check first. */
export const QR_MAX_BYTES =
  LEVEL_M[MAX_VERSION].blocks * LEVEL_M[MAX_VERSION].dataPerBlock - 2;
